"use client";

import type { Variants } from "motion/react";
import { motion, useAnimation } from "motion/react";
import type { HTMLAttributes } from "react";
import React, { forwardRef, useCallback, useImperativeHandle } from "react";

import { cn } from "@/lib/utils";
import { useHoverResetGuard } from "./useHoverResetGuard";

export interface FlaskConicalIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface FlaskConicalIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  /** e.g. `inherit` from sidebar route row so `currentColor` follows the nav icon color */
  color?: string;
}

const LIQUID_VARIANTS: Variants = {
  normal: { y: 0, opacity: 1 },
  animate: {
    y: [0, -1.5, 0],
    opacity: [1, 0.75, 1],
    transition: {
      duration: 1.2,
      repeat: Number.POSITIVE_INFINITY,
      repeatType: "mirror",
      ease: "easeInOut",
    },
  },
};

const SVG_WRAPPER_VARIANTS: Variants = {
  normal: { scale: 1 },
  animate: {
    scale: [1, 1.08, 1],
    transition: {
      duration: 1.2,
      repeat: Number.POSITIVE_INFINITY,
      repeatType: "mirror",
      ease: "easeInOut",
    },
  },
};

const FlaskConicalIcon = forwardRef<FlaskConicalIconHandle, FlaskConicalIconProps>(
  (
    {
      onMouseEnter,
      onMouseLeave,
      className,
      size = 20,
      color,
      style,
      ...props
    },
    ref
  ) => {
    const controls = useAnimation();
    const { withLockedHoverReset, releaseHoverResetLock, shouldApplyHoverReset } =
      useHoverResetGuard();

    useImperativeHandle(
      ref,
      () => ({
        startAnimation: () =>
          void withLockedHoverReset(async () => {
            await controls.start("animate");
          }),
        stopAnimation: () => {
          releaseHoverResetLock();
          void controls.start("normal");
        },
      }),
      [controls, releaseHoverResetLock, withLockedHoverReset]
    );

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        onMouseEnter?.(e);
        void controls.start("animate");
      },
      [controls, onMouseEnter]
    );

    const handleMouseLeave = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        onMouseLeave?.(e);
        if (!shouldApplyHoverReset()) return;
        void controls.start("normal");
      },
      [controls, onMouseLeave, shouldApplyHoverReset]
    );

    return (
      <div
        className={cn(className)}
        style={{ color: color ?? undefined, ...style }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        <motion.svg
          animate={controls}
          fill="none"
          height={size}
          initial="normal"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          variants={SVG_WRAPPER_VARIANTS}
          viewBox="0 0 24 24"
          width={size}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M14 2v6a2 2 0 0 0 .245.96l5.51 10.08A2 2 0 0 1 18 22H6a2 2 0 0 1-1.755-2.96l5.51-10.08A2 2 0 0 0 10 8V2" />
          <path d="M8.5 2h7" />
          <motion.path
            animate={controls}
            d="M6.453 15h11.094"
            initial="normal"
            variants={LIQUID_VARIANTS}
          />
        </motion.svg>
      </div>
    );
  }
);

FlaskConicalIcon.displayName = "FlaskConicalIcon";

export { FlaskConicalIcon };
